import { FC, useCallback, useEffect, useMemo } from "react";

import { FakeProgressOverview } from "../course/progress/FakeProgressOverview";
import { ProgressOverview } from "../course/progress/ProgressOverview";
import { OutcomeChipList } from "../outcomes/OutcomeChipList";

import { HomeAssessmentList } from "./HomeAssessmentList";
import { HomeCourseList } from "./HomeCourseList";
import { HomeOutcomesList } from "./HomeOutcomesList";
import { StudentOutcomeChipList } from "./StudentOutcomeChipList";

import { SECONDARY_OVERLAY_QUERY_PARAM, TAB_QUERY_PARAM } from "components/constants";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { TabBar } from "mds/components/TabBar";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import { AssessPointsMarkerIcon, FlagIcon, OutcomeHashtagIcon, TextBooksIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentUserId, selectOutcomesForCurrentUser } from "store/selectors";
import { useQueryParamsManager } from "utils/routing";
import { HomeRoleType, homeOutcomesUrl, homeUrlForRole, withQueryParams } from "utils/urls";
import { isStudentDemoEnabled } from "utils/waffle-flags";
import { ErrorBoundary } from "worksheets/views/ErrorBoundary";

const HomeContentTabKeys = ["courses", "outcomes", "assessments", "progress"] as const;

export const HomeContentView: FC<{ userRole: HomeRoleType }> = ({ userRole }) => {
  const isStaff = userRole !== "student";
  const isSmallScreen = !useIsMdOrLarger();
  const outcomes = useAppSelector((s) => selectOutcomesForCurrentUser(s, userRole));
  const currentUserId = useAppSelector(selectCurrentUserId);

  const { getQueryParam } = useQueryParamsManager();
  const overlay = getQueryParam(SECONDARY_OVERLAY_QUERY_PARAM);
  const urlForTab = useCallback(
    (tab: (typeof HomeContentTabKeys)[number]) => {
      // This is a fix for switching tabs accidentally closing overlays
      if (overlay) {
        return withQueryParams(homeUrlForRole(userRole), { tab, overlay });
      }
      return withQueryParams(homeUrlForRole(userRole), { tab });
    },
    [overlay, userRole],
  );

  useEffect(() => {
    if (currentUserId) {
      storeApi.course_outcomes.list();
    }
  }, [currentUserId]);

  const ProgressComponent = isStudentDemoEnabled() ? FakeProgressOverview : ProgressOverview;

  const tabs = useMemo(
    () =>
      [
        {
          key: HomeContentTabKeys[0],
          to: urlForTab(HomeContentTabKeys[0]),
          label: (
            <IconText
              iconStart={<TextBooksIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("home.tabs.courses")}
            />
          ),
          title: t("home.tabs.courses"),
          eventName: "Home - View course catalog",
          panel: <HomeCourseList role={userRole} />,
        },
        {
          key: HomeContentTabKeys[1],
          to: urlForTab(HomeContentTabKeys[1]),
          label: (
            <IconText
              iconStart={<OutcomeHashtagIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("home.tabs.outcomes")}
            />
          ),
          title: t("home.tabs.outcomes"),
          eventName: "Home - View outcomes index",
          panel: <HomeOutcomesList role={userRole} />,
        },
        {
          key: HomeContentTabKeys[2],
          to: urlForTab(HomeContentTabKeys[2]),
          label: (
            <IconText
              iconStart={<AssessPointsMarkerIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("glossary.assessments")}
            />
          ),
          title: t("glossary.assessments"),
          eventName: "Home - View assessments",
          panel: <HomeAssessmentList role={userRole} />,
        },
        {
          key: HomeContentTabKeys[3],
          to: urlForTab(HomeContentTabKeys[3]),
          label: (
            <IconText
              iconStart={<FlagIcon className="group-data-[selected]/tab:text-blue" />}
              text={t("progress_overview.title")}
            />
          ),
          title: t("progress_overview.title"),
          eventName: "Home - View progress overview",
          panel: !isStaff ? (
            <ErrorBoundary>
              <ProgressComponent withOutcomeCards />
            </ErrorBoundary>
          ) : null,
        },
      ].filter((tab) => tab.panel),
    [isStaff, userRole, urlForTab, ProgressComponent],
  );

  const activeTab =
    (getQueryParam(TAB_QUERY_PARAM) as (typeof HomeContentTabKeys)[number] | null) ??
    HomeContentTabKeys[0];
  const tabIndex = useMemo(() => tabs.map((tab) => tab.key).indexOf(activeTab), [activeTab, tabs]);

  return (
    <div className="h-full overflow-y-auto bg-white md:overflow-y-hidden">
      <TabBar
        className="md:h-full"
        contentOnly={isSmallScreen}
        selectedIndex={tabIndex}
        tabListClassName="gap-4"
        tabs={tabs}
      />

      {isSmallScreen &&
        outcomes.length > 0 &&
        (isStaff ? (
          <div className="mt-4">
            <OutcomeChipList
              getOutcomeUrl={(outcome) => homeOutcomesUrl(userRole, outcome.id)}
              outcomes={outcomes}
            />
          </div>
        ) : (
          <div className="mt-4">
            <StudentOutcomeChipList currentUserId={currentUserId} outcomes={outcomes} />
            <div className="mt-4 pb-8">
              <h3 className="my-2 flex items-center justify-between text-black-tint-40">
                {t("progress_overview.header")}
              </h3>
              <ProgressComponent />
            </div>
          </div>
        ))}
    </div>
  );
};
